/**
 * The subject fields that must be present before a subject can reach checkout.
 *
 * subjectFirstName, subjectLastName, and subjectGrade are the crash-class fields — the Order
 * domain rejects them as null/blank when the order is flushed during PaymentService.acceptPayment
 * (the first two are `blank: false`, subjectGradeCode is `nullable: false`), so a subject missing
 * any of them fails validation at processPayment with PAYMENTHANDLER_SYSTEM_ERROR.
 *
 * subjectRelationship is nullable on the Order (it does not crash the flush) but is a required
 * business detail collected by the student-details modal; it is included here so the gate and the
 * modal share one definition of "complete".
 */
export interface RequiredSubjectDetails {
  subjectFirstName?: string | null;
  subjectLastName?: string | null;
  subjectGrade?: string | null;
  subjectRelationship?: string | null;
  hasPackagePersonalization?: boolean | null;
  nameOnText?: string | null;
}

/**
 * The Order domain caps subjectFirstName / subjectLastName at `maxSize: 50` (Order.groovy) and
 * rejects blank values (`blank: false`), so a name that is blank/whitespace-only or longer than 50
 * fails the same flush as a null grade. This is the DB limit — distinct from, and looser than, the
 * modal's SUBJECT_NAME_MAX_LENGTH (20). The gate guards the DB boundary; the modal keeps its
 * stricter length + character rules on top.
 */
export const SUBJECT_NAME_MAX_DB_LENGTH = 50;

function isNameWithinFlushLimits(name: string | null | undefined): boolean {
  const trimmed = name?.trim() ?? '';
  return trimmed.length > 0 && trimmed.length <= SUBJECT_NAME_MAX_DB_LENGTH;
}

/**
 * The single definition of which subject fields MUST be present — and, for names, within the Order
 * flush limits — before a subject can reach checkout.
 *
 * Shared by the pre-checkout gate (ShoppingCartStore.getPreorderSubjects) and the student-details
 * modal (StudentDetailsComponent.isFormStateComplete) so the two cannot drift into disagreeing
 * about what "complete" means. That drift is exactly what let a null grade reach processPayment:
 * the gate keyed off last name + a deferred flag and never looked at grade, so a named, gradeless
 * subject read as complete and sailed through to payment.
 *
 * Guards presence for every field, plus the name blank/length limits the Order flush enforces (so
 * a whitespace-only or >50-char name — which would fail the flush just like a null grade — is
 * caught too). The modal layers teacher name, disclaimer acceptance, and stricter name
 * format/length validation on top of this.
 *
 * When hasPackagePersonalization is true, also requires a non-empty nameOnText: a package that
 * includes Name & Grade on All Portraits must have a NOGO name before checkout or the
 * personalization data reaches the Order as null.
 */
export function hasRequiredSubjectDetails(subject: RequiredSubjectDetails): boolean {
  if (
    !isNameWithinFlushLimits(subject.subjectFirstName) ||
    !isNameWithinFlushLimits(subject.subjectLastName) ||
    !Boolean(subject.subjectGrade) ||
    !Boolean(subject.subjectRelationship)
  ) {
    return false;
  }

  if (subject.hasPackagePersonalization) {
    const resolvedName = subject.nameOnText?.trim() || subject.subjectFirstName?.trim();
    return Boolean(resolvedName);
  }

  return true;
}
